import React, { useState, useEffect } from 'react';
import Navigation from '@/components/shared/Navigation';
import PremiumDigitalCard from '@/components/cards/PremiumDigitalCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Download, Mail, QrCode, Share, Eye, ExternalLink, Sparkles, RotateCcw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { digitalCardsApi } from '@/lib/api';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import { toast } from '@/components/ui/use-toast';
import { DigitalCard, DigitalCardResponse } from '@/types';
import { BASE_URL } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const MyCard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [cardData, setCardData] = useState<DigitalCardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPremiumLoading, setIsPremiumLoading] = useState(false);
  const [premiumCardHtml, setPremiumCardHtml] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchCardData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await digitalCardsApi.getMyCard();
      
      // Type guard to validate response shape
      const isValidCardResponse = (data: any): data is DigitalCardResponse => {
        console.log("Validating card response:", data);
        console.log("Checking data.card:", !!data.card);
        console.log("Checking data.status_info:", !!data.status_info);
        console.log("Checking data.template_data:", !!data.template_data);
        console.log("Checking data.card.card_number:", typeof data.card?.card_number === 'string');
        console.log("Checking data.template_data.member.full_name:", typeof data.template_data?.member?.full_name === 'string');
        console.log("Checking data.template_data.card_data.member_type:", typeof data.template_data?.card_data?.member_type === 'string');

        return data && 
          data.card &&
          data.status_info &&
          data.template_data &&
          typeof data.card.card_number === 'string' &&
          typeof data.template_data.member.full_name === 'string' &&
          typeof data.template_data.card_data.member_type === 'string';
      };

      if (!isValidCardResponse(response)) {
        console.error('Invalid card data received:', response);
        setError('invalid_card_data');
        setCardData(null);
        return;
      }

      setCardData(response);
    } catch (error: any) {
      console.error('Error fetching card data:', error);
      if (error.response?.status === 404) {
        setError('no_card_generated');
      } else if (error.response?.status === 403) {
        setError('not_authorized');
      } else {
        setError('fetch_error');
      }
      setCardData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      // No need for cardData?.card.id here as it's a self-service endpoint
      const blob = await digitalCardsApi.downloadMyCard();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Use cardData.card.card_number for the filename
      a.download = `digital-card-${cardData?.card.card_number || 'my-card'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({
        title: "Success",
        description: "Digital card downloaded successfully",
      });
    } catch (error) {
      console.error('Error downloading card:', error);
      toast({
        title: "Error",
        description: "Failed to download digital card",
        variant: "destructive",
      });
    }
  };

  const handleEmailCard = async (email: string) => {
    try {
      // No need for cardData?.card.id here as it's a self-service endpoint
      await digitalCardsApi.emailMyCard(email);
      toast({
        title: "Success",
        description: "Digital card sent to your email",
      });
    } catch (error) {
      console.error('Error emailing card:', error);
      toast({
        title: "Error",
        description: "Failed to send digital card",
        variant: "destructive",
      });
    }
  };

  const handleViewPremium = async () => {
    try {
      setIsPremiumLoading(true);
      const response = await digitalCardsApi.getMyPremiumCard();
      
      if (typeof response !== 'string') {
        throw new Error('Invalid premium card response');
      }
      
      setPremiumCardHtml(response);
    } catch (error) {
      console.error('Error fetching premium card:', error);
      toast({
        title: "Error",
        description: "Failed to load premium card view",
        variant: "destructive",
      });
    } finally {
      setIsPremiumLoading(false);
    }
  };

  const handleRegenerateCard = async () => {
    try {
      if (!user?.id) return;
      setIsLoading(true);
      await digitalCardsApi.generate(user.id);
      await fetchCardData();
      toast({
        title: "Success",
        description: "Digital card regenerated successfully",
      });
    } catch (error) {
      console.error('Error regenerating card:', error);
      toast({
        title: "Error",
        description: "Failed to regenerate digital card",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchCardData();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-blue-50">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {isLoading ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <Card className="w-full">
              <CardHeader>
                <CardTitle className="text-center text-red-500">
                  {error === 'no_card_generated' ? (
                    'No Digital Card Found'
                  ) : error === 'invalid_card_data' ? (
                    'Invalid Card Data'
                  ) : error === 'not_authorized' ? (
                    'Not Authorized'
                  ) : (
                    'Error Loading Card'
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <p className="text-gray-600 mb-6">
                  {error === 'no_card_generated' ? (
                    'You don\'t have a digital card yet. Would you like to generate one?'
                  ) : error === 'invalid_card_data' ? (
                    'Your card data appears to be invalid. Please try regenerating your card.'
                  ) : error === 'not_authorized' ? (
                    'You are not authorized to view this card. Please contact support.'
                  ) : (
                    'There was an error loading your card. Please try again later.'
                  )}
                </p>
                {(error === 'no_card_generated' || error === 'invalid_card_data') && (
                  <Button 
                    onClick={handleRegenerateCard}
                    className="bg-basketball-orange-500 hover:bg-basketball-orange-600"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Generate Card
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : cardData && (
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="w-full md:w-7/12">
                  <PremiumDigitalCard card={cardData} />
                </div>
                <div className="w-full md:w-5/12 space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold">Card Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Status</span>
                        <Badge 
                          variant={cardData.status_info.is_active ? 'default' : 'destructive'}
                          className="capitalize"
                        >
                          {cardData.status_info.status_text}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Card Number</span>
                        <span className="font-mono">{cardData.card.card_number}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Issued</span>
                        <span>{new Date(cardData.card.issued_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Expires</span>
                        <span>{new Date(cardData.card.expiry_date).toLocaleDateString()}</span>
                      </div>
                      {cardData.status_info.days_until_expiry <= 30 && cardData.status_info.days_until_expiry > 0 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-yellow-800 text-sm">
                          ⚠️ Your card will expire in {cardData.status_info.days_until_expiry} days
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <div className="grid grid-cols-2 gap-4">
                    <Button 
                      onClick={handleDownload}
                      className="w-full bg-basketball-blue-600 hover:bg-basketball-blue-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button className="w-full">
                          <Mail className="w-4 h-4 mr-2" />
                          Email
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Email Digital Card</DialogTitle>
                          <DialogDescription>
                            Send your digital card to your email address
                          </DialogDescription>
                        </DialogHeader>
                        <Button 
                          onClick={() => handleEmailCard(user?.email || '')}
                          className="w-full"
                        >
                          Send to my email
                        </Button>
                      </DialogContent>
                    </Dialog>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full">
                          <QrCode className="w-4 h-4 mr-2" />
                          QR Code
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>QR Code</DialogTitle>
                          <DialogDescription>
                            Scan this QR code to verify the card
                          </DialogDescription>
                        </DialogHeader>
                        <div className="flex justify-center p-4">
                          <img 
                            src={`${BASE_URL}${cardData.template_data.qr_code_url}`} 
                            alt="QR Code"
                            className="w-48 h-48"
                          />
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          className="w-full"
                          onClick={handleViewPremium}
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          Premium
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl">
                        <DialogHeader>
                          <DialogTitle>Premium Digital Card</DialogTitle>
                        </DialogHeader>
                        {isPremiumLoading ? (
                          <div className="flex justify-center p-8">
                            <LoadingSpinner />
                          </div>
                        ) : premiumCardHtml ? (
                          <PremiumDigitalCard htmlContent={premiumCardHtml} />
                        ) : (
                          <p className="text-center text-gray-500">
                            Premium card view not available
                          </p>
                        )}
                      </DialogContent>
                    </Dialog>
                  </div>

                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={handleRegenerateCard}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Regenerate Card
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyCard;
